// World/time layer for Toast Town.
// A single shared clock drives day/night, shop hours, and action budgeting.
// Scenes call advanceTime(hours) after activities; sleeping rolls to morning.

import { getState, save } from './save.js';

const listeners = new Set();
export function onTimeChange(fn){ listeners.add(fn); return () => listeners.delete(fn); }
function emit(){
  const t = getTime();
  _updateBodyClass(t.hour);
  listeners.forEach(fn => fn(t));
}

function _updateBodyClass(hour){
  const body = document.body;
  if (!body) return;
  body.classList.remove('time-dawn', 'time-day', 'time-dusk', 'time-night');
  body.classList.add('time-' + getPhase(hour));
}

export function getTime(){
  const s = getState();
  return { hour: s.world.hour, day: s.world.day };
}

// initialize body class immediately in case import runs after DOM is ready
if (typeof document !== 'undefined'){
  _updateBodyClass(getHour());
}

export function getHour(){ return getState().world.hour; }
export function getDay(){ return getState().world.day; }

const SEASON_DAYS = 7;
export const SEASONS = ['spring', 'summer', 'autumn', 'winter'];
export function getSeason(day = getDay()){
  const i = Math.floor((day - 1) / SEASON_DAYS) % SEASONS.length;
  return SEASONS[i];
}
export function getSeasonProgress(day = getDay()){
  const i = Math.floor((day - 1) / SEASON_DAYS) % SEASONS.length;
  const d = ((day - 1) % SEASON_DAYS) + 1;
  return { season: SEASONS[i], index: i, dayInSeason: d, daysLeft: SEASON_DAYS - d + 1 };
}

// 06:00–20:00 is "day" for shop/NPC purposes
export function isDaytime(){ const h = getHour(); return h >= 6 && h < 20; }
export function isNight(){ return !isDaytime(); }

// Advance the clock. Activities typically cost 0.5–2 hours.
export function advanceTime(hours = 0.5){
  const s = getState();
  s.world.hour += hours;
  while (s.world.hour >= 24){
    s.world.hour -= 24;
    s.world.day++;
  }
  save();
  emit();
}

// Sleep until morning (06:00). If already morning, sleep until next morning.
export function sleepUntilMorning(){
  const s = getState();
  if (s.world.hour < 6){
    s.world.hour = 6;
  } else {
    s.world.hour = 6;
    s.world.day++;
  }
  save();
  emit();
}

// Format like "7:00 AM" / "9:30 PM"
export function formatTime(hour = getHour()){
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const ampm = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 || 12;
  return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Day-phase label used for lighting / ambience
export function getPhase(hour = getHour()){
  if (hour < 5)  return 'night';
  if (hour < 7)  return 'dawn';
  if (hour < 17) return 'day';
  if (hour < 20) return 'dusk';
  return 'night';
}

// Shop hours can be 'always', 'day', 'night', or { open, close } in 24-hour time.
export function isOpen(hours = 'always'){
  if (hours === 'always') return true;
  if (hours === 'day') return isDaytime();
  if (hours === 'night') return isNight();
  const h = getHour();
  if (hours && typeof hours === 'object'){
    return h >= hours.open && h < hours.close;
  }
  return true;
}

export function openLabel(hours = 'always'){
  if (hours === 'always') return 'open';
  if (hours === 'day') return 'open during the day';
  if (hours === 'night') return 'open at night';
  if (hours && typeof hours === 'object'){
    const fmt = (n) => {
      const ampm = n < 12 ? 'AM' : 'PM';
      const hr = n % 12 || 12;
      return `${hr} ${ampm}`;
    };
    return `${fmt(hours.open)} – ${fmt(hours.close)}`;
  }
  return 'open';
}
